import { v4 } from 'uuid';

import { type AddonItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { type Attendee, type Event } from 'hb-react/shared/apps/Events/types';
import {
  type AddonCartItemAttributes,
  type ReceiptAttributes,
  type TicketCartItemAttributes,
} from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { type User } from 'hb-react/types';

const getSubAddon = (
  addon: AddonItem,
  attendee: Attendee,
): AddonCartItemAttributes => {
  const {
    email,
    firstname,
    lastname,
    user_id: userId,
    guest_id: guestId,
    ticket: { id: ticketId },
    metadata,
  } = attendee;
  const { id, cartItem, customizable_attributes: attributes } = addon;

  return {
    line_itemable_type: 'Ecommerce::Products::EventAddon',
    line_itemable_id: id,
    quantity: cartItem?.quantity || 1,
    currency: addon.currency,
    metadata: {
      event: {
        ...addon?.cartItem?.metadata?.event,
        ...attributes.reduce(
          (attributes, attribute) => ({
            [attribute.name]: addon[attribute.name],
            ...attributes,
          }),
          {},
        ),
        uuid: v4(),
        addon_id: id,
        email,
        firstname,
        lastname,
        user_id: userId,
        guest_id: guestId,
        ticket_id: ticketId,
        parent_uuid: metadata?.event?.uuid,
        type: 'sub_addon',
      },
    },
  };
};

const getStandaloneAddons = (
  addons: Array<AddonItem>,
  attendee: Attendee,
): Array<AddonCartItemAttributes> =>
  addons
    .filter(({ cartItem }) => !!cartItem && cartItem?.quantity > 0)
    .map(({ id, currency, customizable_attributes: attributes, ...addon }) => ({
      line_itemable_type: 'Ecommerce::Products::EventAddon',
      line_itemable_id: id,
      quantity: 1,
      currency,
      metadata: {
        event: {
          ...addon?.cartItem?.metadata?.event,
          ...attributes.reduce(
            (attributes, attribute) => ({
              [attribute.name]: addon[attribute.name],
              ...attributes,
            }),
            {},
          ),
          email: attendee.email,
          firstname: attendee.firstname,
          lastname: attendee.lastname,
          user_id: attendee.user_id,
          guest_id: attendee.guest_id,
          addon_id: id,
          type: 'standalone_addon',
        },
      },
    }));

export const transform = ({
  attendees,
  addons,
  event,
  currentUser,
}: {
  addons: Array<AddonItem>;
  attendees: Array<Attendee>;
  currentUser: User | null;
  event: Event;
}): {
  cartItems: Array<AddonCartItemAttributes | TicketCartItemAttributes>;
  receipt: ReceiptAttributes;
} => {
  const cartItems = attendees.flatMap(
    (attendee): Array<AddonCartItemAttributes | TicketCartItemAttributes> => {
      const cartTicket = attendee.ticket;

      const {
        email,
        firstname,
        lastname,
        metadata,
        addons,
        ticket,
        ...attendeePayload
      } = attendee;

      const customizableAttributes = [
        ...ticket.customizable_attributes,
        ...Object.values(event.standard_fields),
      ].reduce(
        (attributes, attribute) => ({
          [attribute.name]: attendee[attribute.name],
          ...attributes,
        }),
        {},
      );

      const ticketAttendee: TicketCartItemAttributes = {
        line_itemable_type: 'NetworkEvents::Ticket',
        line_itemable_id: cartTicket.id,
        currency: cartTicket.currency,
        quantity: 1,
        metadata: {
          event: {
            ...metadata?.event,
            ...customizableAttributes,
            ...attendeePayload,
            uuid: metadata?.event?.uuid,
            lastname,
            firstname,
            email,
            ticket_holder: attendee.ticket_holder,
            guest_id: attendee.guest_id,
            ticket_id: cartTicket.id,
            type: 'ticket',
          },
        },
      };

      const subAddons = attendee.addons
        .filter((addon) => addon.cartItem && addon.cartItem?.quantity > 0)
        .map((addon) => getSubAddon(addon, attendee));

      return [ticketAttendee, ...subAddons];
    },
  );

  const standaloneAddons = getStandaloneAddons(addons, attendees[0]);

  return {
    cartItems: [...cartItems, ...standaloneAddons],
    receipt: {
      firstname: currentUser?.firstname ?? attendees[0]?.firstname ?? '',
      lastname: currentUser?.lastname ?? attendees[0]?.lastname ?? '',
      email: currentUser?.email ?? attendees[0]?.email ?? '',
    },
  };
};
